/**
 * Engineering considerations.
 *
 * Every entry is a question. None is a conclusion, a requirement, a pass/fail
 * signal, or a suitability determination. Text lives here as data so it can be
 * revised without touching application logic.
 *
 * PROVENANCE NOTE (important):
 * The evidence map referenced by the build specification — "Human-Centered
 * Accelerated Aging Decision-Support Tool — Proof of Concept: Structured
 * Scoping Review and Evidence Map" — was not supplied to this build. The
 * question framings below are therefore taken from the build specification
 * itself, not from the underlying research. They carry the tier
 * `specification-derived`. When the evidence map is available, each entry
 * should be re-tiered against what the research actually supports, and the
 * `basis` field updated to cite it.
 */

import type { ChecklistItem, EngineeringConsideration } from '../types';

export const ENGINEERING_CONSIDERATIONS: EngineeringConsideration[] = [
  {
    id: 'q10-assumption',
    category: 'Q10 assumption',
    question:
      'Is the selected Q10 assumption appropriate for the relevant materials, degradation mechanisms, and available supporting information?',
    context:
      'Q10 is an assumed multiplier, not a measured property of your system. The calculated duration scales with it directly — the sensitivity table shows by how much for this scenario.',
    basis: 'Framing taken from the build specification for this proof of concept.',
    basisTier: 'specification-derived',
  },
  {
    id: 'temperature-material-behaviour',
    category: 'Temperature and material behaviour',
    question:
      'Could the selected accelerated temperature introduce material behaviour or degradation mechanisms that would not reasonably represent real-time storage?',
    context:
      'The calculation treats the relationship between temperature and rate as a single exponential factor. It has no knowledge of your materials and cannot detect a transition the model does not represent.',
    basis:
      'Framing taken from the build specification. A related concern appears in ASTM work item WK94053, a proposed revision of F1980-21; the work item record is publicly listed, but its rationale is a proposal under development, not an adopted requirement.',
    basisTier: 'specification-derived',
  },
  {
    id: 'humidity',
    category: 'Humidity',
    question:
      'Could moisture or humidity materially influence the product, sterile barrier system, adhesive system, porous component, or packaging material?',
    context:
      'This calculation contains no humidity term. Whether humidity needs to be controlled, and at what level, is outside what this tool models or decides.',
    basis: 'Framing taken from the build specification for this proof of concept.',
    basisTier: 'specification-derived',
  },
  {
    id: 'real-time-aging',
    category: 'Real-time aging',
    question:
      'Is the appropriate real-time aging programme being conducted to support or confirm the shelf-life evaluation?',
    context:
      'An accelerated result is a modelled estimate. This tool produces the estimate; it does not produce evidence about your system.',
    basis:
      'Framing taken from the build specification. Publicly accessible scope summaries of ASTM F1980-21 also describe real-time aging as outside that guide’s scope and as an ISO 11607-1 matter; the licensed standards were not reviewed in full for this build.',
    basisTier: 'specification-derived',
  },
  {
    id: 'standards-procedures',
    category: 'Standards and procedures',
    question:
      'Have the current licensed standards, internal procedures, risk documentation, and applicable regulatory requirements been reviewed?',
    context:
      'This tool reproduces no normative text and tracks no revision status. Requirements must be read from controlled copies of the current editions.',
    basis: 'Framing taken from the build specification for this proof of concept.',
    basisTier: 'specification-derived',
  },
];

export const CONSIDERATION_CHECKLIST: ChecklistItem[] = [
  { id: 'chk-q10-basis', label: 'I have documented the basis for the Q10 assumption.' },
  {
    id: 'chk-temp-behaviour',
    label: 'I have considered whether the selected temperature could alter relevant material behaviour.',
  },
  { id: 'chk-humidity', label: 'I have considered whether humidity is relevant to this system.' },
  {
    id: 'chk-not-validity',
    label: 'I understand that the calculation does not establish final shelf-life validity by itself.',
  },
  { id: 'chk-standards', label: 'I will evaluate applicable current standards and internal requirements.' },
];
